import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { retrieveOneBaselineItem } from "../slices/baselineSlice";
import { useDialogBox } from "./useDialogBox";
import { prepareBaselineItemObject } from "../utils/baselineUtils";
import { debugLog, errorLog } from "../utils/debugUtils";

/**
 * RAVEN pattern: centralize all BaselineItems (step 3) logic/data in a custom hook.
 * - Handles current item selection, fetch, prep, and local state management.
 * - Exposes all state and handlers for parent (BaselineItems.jsx).
 *
 * @param {Object} currBaseline The parent Baseline (cloned from Redux)
 * @returns {{
 *   currentItem: Object,
 *   setCurrentItem: Function,
 *   itemList: Array,
 *   handleSelectBaselineItem: Function,
 *   clearCurrentItem: Function,
 *   fetchAndPrepareItem: Function
 * }}
 */
export function useBaselineItemsData(currBaseline, lookups, fetchBaselineByIdOrUuid, onSetDataChanged) {
	const dispatch = useDispatch();
	const { withError } = useDialogBox();

	// List of items for the table
	const [itemList, setItemList] = useState(currBaseline?.baseline_items || []);
	// Current item being edited/created
	const [currentItem, setCurrentItem] = useState(null);

	// Sync itemList to baseline change
	useEffect(() => {
		debugLog(3, "[useBaselineItemsData][STATE] currBaseline changed", { baselineId: currBaseline?.id });

		setItemList(currBaseline?.baseline_items || []);
	}, [currBaseline]);

	// BaselineMain / ScenarioMain already surface top-level retrieval failures from
	// slice state, but this nested item-detail hook has no page-level watcher to do
	// that work for us. When a Baseline item open/load fails, show the shared dialog
	// here so Baseline Items and Scenario Selections do not fail silently.
	//
	// Item-detail fetches are read-only, so retry is safe and should stay available.
	const surfaceItemFetchError = useCallback(
		(error, itemId, retryAction) => {
			errorLog(2, "[useBaselineItemsData][FLOW] retrieveOneBaselineItem failed", {
				itemId,
				error,
			});
			console.error(error);
			withError(error, retryAction, {
				fallbackMessage: "Baseline item retrieve failed.",
			});
		},
		[withError]
	);

	// Handler to select an item (from table row click)
	const handleSelectBaselineItem = useCallback(
		(item) => {
			debugLog(3, "[useBaselineItemsData][ACTION] handleSelectBaselineItem", { itemId: item.id });
			debugLog(2, "[useBaselineItemsData][FLOW] retrieveOneBaselineItem started", { itemId: item.id });

			dispatch(retrieveOneBaselineItem(item.id))
				.unwrap()
				.then((response) => {
					const preparedItem = prepareBaselineItemObject(currBaseline, response, lookups);

					debugLog(3, "[useBaselineItemsData][STATE] baseline item loaded", {
						baselineItem: preparedItem,
					});

					setCurrentItem(preparedItem);
				})
				.catch((err) => {
					// Re-run the same item-open path if the user chooses Retry.
					surfaceItemFetchError(err, item.id, () => handleSelectBaselineItem(item));
				});
		},
		[dispatch, currBaseline, lookups, surfaceItemFetchError]
	);

	// Handler to clear/deselect the current item (return to table/list)
	const clearCurrentItem = useCallback(() => {
		debugLog(3, "[useBaselineItemsData][ACTION] clearCurrentItem", {});
		setCurrentItem(null);
		if (fetchBaselineByIdOrUuid && currBaseline?.id) {
			debugLog(2, "[useBaselineItemsData][FLOW] fetchBaselineByIdOrUuid started", {
				idOrUuid: currBaseline.id,
			});
			fetchBaselineByIdOrUuid(currBaseline.id); // refresh parent baseline
		}
		if (onSetDataChanged) onSetDataChanged(false);

		debugLog(3, "[useBaselineItemsData][STATE] current item cleared", {});
	}, [fetchBaselineByIdOrUuid, currBaseline, onSetDataChanged]);

	// Handler to fetch and prep a specific item by id (utility)
	const fetchAndPrepareItem = useCallback(
		(itemId) => {
			debugLog(3, "[useBaselineItemsData][ACTION] fetchAndPrepareItem", { itemId: itemId });
			debugLog(2, "[useBaselineItemsData][FLOW] retrieveOneBaselineItem started", { itemId: itemId });

			dispatch(retrieveOneBaselineItem(itemId))
				.unwrap()
				.then((response) => {
					// setCurrentItem(prepareBaselineItemObject(currBaseline, response, lookups));
					const preparedItem = prepareBaselineItemObject(currBaseline, response, lookups);
					debugLog(3, "[useBaselineItemsData][STATE] baseline item loaded by id", {
						baselineItem: preparedItem,
					});
					setCurrentItem(preparedItem);
				})
				.catch((err) => {
					// Re-run the same direct item fetch if the user chooses Retry.
					surfaceItemFetchError(err, itemId, () => fetchAndPrepareItem(itemId));
				});
		},
		[dispatch, currBaseline, lookups, surfaceItemFetchError]
	);

	// Optionally: expose setItemList if you want local updates without a baseline reload

	return {
		itemList,
		currentItem,
		setCurrentItem,
		handleSelectBaselineItem,
		clearCurrentItem,
		fetchAndPrepareItem,
	};
}
